import React, { useCallback, useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { PageHeader, PageBody } from "@/components/PageParts";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Upload, Trash2, Palette, FileText, ExternalLink, RefreshCw, Mail } from "lucide-react";

const BLANK = {
  company_name: "", company_registration: "", address_lines: ["", "", ""],
  phone: "", email: "", tin: "", sst_number: "",
  brand_color: "#0f172a", brand_text_on_color: "#ffffff", initials: "AE",
  footer_note: "", account_name: "", account_bank: "", account_number: "",
  logo_data_url: null,
};

const API_BASE = process.env.REACT_APP_BACKEND_URL;

export default function BrandingSettings() {
  const [state, setState] = useState(BLANK);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [previewKey, setPreviewKey] = useState(0);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const fileRef = useRef();

  const loadPreview = useCallback(async () => {
    setPreviewLoading(true);
    try {
      const { data } = await api.get("/settings/branding/preview", {
        responseType: "blob", params: { format: "png" },
      });
      const url = URL.createObjectURL(data);
      setPreviewUrl((prev) => { if (prev) URL.revokeObjectURL(prev); return url; });
    } catch (err) {
      toast.error("Could not render preview: " + (formatApiError(err.response?.data?.detail) || err.message));
    } finally {
      setPreviewLoading(false);
    }
  }, []);

  useEffect(() => () => { if (previewUrl) URL.revokeObjectURL(previewUrl); }, [previewUrl]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/settings/branding");
      const lines = (data.address_lines || []).concat(["", "", ""]).slice(0, 3);
      setState({ ...BLANK, ...data, address_lines: lines });
      loadPreview();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  }, [loadPreview]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...state };
      delete payload.logo_data_url;
      payload.address_lines = state.address_lines.map((l) => l.trim()).filter(Boolean);
      const { data } = await api.put("/settings/branding", payload);
      const lines = (data.address_lines || []).concat(["", "", ""]).slice(0, 3);
      setState({ ...BLANK, ...data, address_lines: lines });
      setPreviewKey((k) => k + 1);
      loadPreview();
      toast.success("Branding saved. New PDFs will use these details.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  const onUpload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", f);
    try {
      const { data } = await api.post("/settings/branding/logo", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setState((s) => ({ ...s, logo_data_url: data.logo_data_url }));
      setPreviewKey((k) => k + 1);
      loadPreview();
      toast.success("Logo uploaded");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const removeLogo = async () => {
    try {
      const { data } = await api.delete("/settings/branding/logo");
      setState((s) => ({ ...s, logo_data_url: data.logo_data_url }));
      setPreviewKey((k) => k + 1);
      loadPreview();
      toast.success("Logo cleared");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const setLine = (i, v) => setState((s) => {
    const next = [...s.address_lines]; next[i] = v; return { ...s, address_lines: next };
  });

  if (loading) return <PageBody><div className="text-slate-500 text-sm">Loading branding…</div></PageBody>;

  return (
    <>
      <PageHeader title="Invoice, Pro-forma & Receipt Branding" subtitle="Everything on the top of every PDF and email is driven from here — logo, colour, address, tax IDs, bank details and footer.">
        <Button variant="outline" onClick={load} data-testid="branding-refresh"><RefreshCw className="w-4 h-4 mr-1.5" />Reload</Button>
        <Button onClick={save} disabled={saving} data-testid="branding-save">{saving ? "Saving…" : "Save changes"}</Button>
      </PageHeader>
      <PageBody>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Left column: logo + colour + preview */}
          <div className="lg:col-span-1 space-y-4">
            <section className="bg-white border border-slate-200 rounded-md p-5 shadow-sm space-y-3">
              <h3 className="text-sm font-semibold text-slate-900 flex items-center gap-2"><Palette className="w-4 h-4" />Logo & colour</h3>

              <div>
                <Label className="text-xs uppercase text-slate-500">Logo</Label>
                <div className="mt-2 flex items-center gap-3">
                  <div className="w-20 h-20 rounded flex items-center justify-center overflow-hidden border border-slate-200"
                       style={{ background: state.brand_color, color: state.brand_text_on_color }}
                       data-testid="branding-logo-preview">
                    {state.logo_data_url
                      ? <img src={state.logo_data_url} alt="logo" className="max-w-full max-h-full object-contain" />
                      : <span className="text-sm font-bold tracking-wide">{state.initials || "AE"}</span>}
                  </div>
                  <div className="flex-1 space-y-2">
                    <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml" className="hidden" onChange={onUpload} data-testid="branding-logo-input" />
                    <Button variant="outline" size="sm" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="branding-logo-upload">
                      <Upload className="w-3.5 h-3.5 mr-1.5" />{uploading ? "Uploading…" : (state.logo_data_url ? "Replace logo" : "Upload logo")}
                    </Button>
                    {state.logo_data_url && (
                      <Button variant="ghost" size="sm" onClick={removeLogo} className="text-rose-700 hover:text-rose-800" data-testid="branding-logo-remove">
                        <Trash2 className="w-3.5 h-3.5 mr-1.5" />Remove
                      </Button>
                    )}
                    <p className="text-[11px] text-slate-500">PNG / JPEG / WEBP / SVG · under 400 KB · square works best.</p>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs uppercase text-slate-500">Brand colour</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <input type="color" value={state.brand_color} onChange={(e) => setState({ ...state, brand_color: e.target.value })} className="h-9 w-14 rounded border border-slate-200 cursor-pointer" data-testid="branding-color" />
                    <Input value={state.brand_color} onChange={(e) => setState({ ...state, brand_color: e.target.value })} className="font-mono text-xs" maxLength={7} />
                  </div>
                </div>
                <div>
                  <Label className="text-xs uppercase text-slate-500">Text on brand</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <input type="color" value={state.brand_text_on_color} onChange={(e) => setState({ ...state, brand_text_on_color: e.target.value })} className="h-9 w-14 rounded border border-slate-200 cursor-pointer" data-testid="branding-text-color" />
                    <Input value={state.brand_text_on_color} onChange={(e) => setState({ ...state, brand_text_on_color: e.target.value })} className="font-mono text-xs" maxLength={7} />
                  </div>
                </div>
              </div>

              <div>
                <Label className="text-xs uppercase text-slate-500">Initials (fallback when no logo)</Label>
                <Input value={state.initials} onChange={(e) => setState({ ...state, initials: e.target.value.toUpperCase() })} maxLength={4} className="mt-1 font-mono uppercase" data-testid="branding-initials" />
              </div>
            </section>

            <section className="bg-white border border-slate-200 rounded-md p-5 shadow-sm space-y-3">
              <h3 className="text-sm font-semibold text-slate-900 flex items-center gap-2"><FileText className="w-4 h-4" />Live preview</h3>
              <p className="text-xs text-slate-500">Refreshes after every Save. Demo invoice using your current branding.</p>
              <div className="border border-slate-200 rounded overflow-hidden bg-slate-50 relative" data-testid="branding-preview-frame">
                {previewLoading && <div className="absolute inset-0 flex items-center justify-center text-xs text-slate-500 bg-white/60 z-10">Rendering…</div>}
                {previewUrl ? (
                  <img
                    src={previewUrl}
                    alt="Branding preview"
                    className="w-full block"
                    style={{ maxHeight: "560px", objectFit: "contain", background: "#ffffff" }}
                    data-testid="branding-preview-image"
                  />
                ) : (
                  <div className="p-8 text-center text-xs text-slate-400">Preview will appear here after loading.</div>
                )}
              </div>
              <div className="flex flex-wrap gap-2">
                <Button variant="outline" size="sm" onClick={async () => {
                  const { data } = await api.get("/settings/branding/preview", { responseType: "blob" });
                  const url = URL.createObjectURL(data);
                  window.open(url, "_blank");
                }} data-testid="branding-open-preview"><ExternalLink className="w-3.5 h-3.5 mr-1.5" />Open full PDF</Button>
                <Button variant="ghost" size="sm" onClick={loadPreview} disabled={previewLoading} data-testid="branding-refresh-preview"><RefreshCw className="w-3.5 h-3.5 mr-1.5" />Refresh preview</Button>
              </div>
            </section>
          </div>

          {/* Right column: company & contact & bank */}
          <div className="lg:col-span-2 space-y-4">
            <section className="bg-white border border-slate-200 rounded-md p-5 shadow-sm space-y-3">
              <h3 className="text-sm font-semibold text-slate-900">Company header</h3>
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2">
                  <Label className="text-xs uppercase text-slate-500">Legal name</Label>
                  <Input value={state.company_name} onChange={(e) => setState({ ...state, company_name: e.target.value })} data-testid="branding-company-name" />
                </div>
                <div className="col-span-2">
                  <Label className="text-xs uppercase text-slate-500">Company registration</Label>
                  <Input value={state.company_registration} onChange={(e) => setState({ ...state, company_registration: e.target.value })} placeholder="e.g. 202401045105 (1590951-D)" data-testid="branding-registration" />
                </div>
                {state.address_lines.map((line, i) => (
                  <div className="col-span-2" key={`address-line-${i}`}>
                    <Label className="text-xs uppercase text-slate-500">Address line {i + 1}</Label>
                    <Input value={line} onChange={(e) => setLine(i, e.target.value)} data-testid={`branding-address-${i}`} />
                  </div>
                ))}
                <div>
                  <Label className="text-xs uppercase text-slate-500">Phone</Label>
                  <Input value={state.phone} onChange={(e) => setState({ ...state, phone: e.target.value })} data-testid="branding-phone" />
                </div>
                <div>
                  <Label className="text-xs uppercase text-slate-500">Email</Label>
                  <Input value={state.email} onChange={(e) => setState({ ...state, email: e.target.value })} data-testid="branding-email" />
                </div>
                <div>
                  <Label className="text-xs uppercase text-slate-500">TIN No</Label>
                  <Input value={state.tin} onChange={(e) => setState({ ...state, tin: e.target.value })} className="font-mono" data-testid="branding-tin" />
                </div>
                <div>
                  <Label className="text-xs uppercase text-slate-500">SST No</Label>
                  <Input value={state.sst_number} onChange={(e) => setState({ ...state, sst_number: e.target.value })} className="font-mono" data-testid="branding-sst" />
                </div>
              </div>
            </section>

            <section className="bg-white border border-slate-200 rounded-md p-5 shadow-sm space-y-3">
              <h3 className="text-sm font-semibold text-slate-900">Bank account (invoice payment block)</h3>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs uppercase text-slate-500">Account name</Label>
                  <Input value={state.account_name} onChange={(e) => setState({ ...state, account_name: e.target.value })} data-testid="branding-account-name" />
                </div>
                <div>
                  <Label className="text-xs uppercase text-slate-500">Bank</Label>
                  <Input value={state.account_bank} onChange={(e) => setState({ ...state, account_bank: e.target.value })} data-testid="branding-account-bank" />
                </div>
                <div className="col-span-2">
                  <Label className="text-xs uppercase text-slate-500">Account number</Label>
                  <Input value={state.account_number} onChange={(e) => setState({ ...state, account_number: e.target.value })} className="font-mono" data-testid="branding-account-number" />
                </div>
              </div>
            </section>

            <section className="bg-white border border-slate-200 rounded-md p-5 shadow-sm space-y-3">
              <h3 className="text-sm font-semibold text-slate-900">Footer note</h3>
              <Textarea rows={2} value={state.footer_note} onChange={(e) => setState({ ...state, footer_note: e.target.value })} maxLength={400} data-testid="branding-footer" />
              <p className="text-[11px] text-slate-500">Printed at the bottom of receipts and invoices. Keep it short — one sentence works best.</p>
            </section>
          </div>
        </div>

        <div className="mt-6">
          <EmailTemplatesPanel />
        </div>
      </PageBody>
    </>
  );
}

// -------------------- Email templates panel --------------------
const TEMPLATE_META = {
  invoice: {
    label: "Invoice email",
    tokens: [
      ["{number}", "Invoice number"],
      ["{name}", "Recipient name"],
      ["{company}", "Recipient company"],
      ["{total}", "Total (e.g. MYR 10,000.00)"],
      ["{currency}", "Currency code"],
      ["{date}", "Issue date"],
    ],
  },
  receipt: {
    label: "Receipt email",
    tokens: [
      ["{number}", "Official Receipt number"],
      ["{name}", "Payer name"],
      ["{amount}", "Amount received"],
      ["{currency}", "Currency code"],
      ["{date}", "Received date"],
      ["{reference}", "Bank / gateway reference"],
      ["{method}", "Payment method"],
    ],
  },
  proforma: {
    label: "Pro-forma / Quotation email",
    tokens: [
      ["{number}", "Pro-forma number"],
      ["{name}", "Recipient name"],
      ["{company}", "Recipient company"],
      ["{kind}", "Document type (Pro-forma / Quotation)"],
      ["{total}", "Total"],
      ["{currency}", "Currency code"],
      ["{date}", "Issue date"],
    ],
  },
};

function EmailTemplatesPanel() {
  const [templates, setTemplates] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/settings/branding/email-templates");
      setTemplates(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const setField = (key, field, value) => setTemplates((t) => ({ ...t, [key]: { ...t[key], [field]: value } }));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/settings/branding/email-templates", templates);
      setTemplates(data);
      toast.success("Email templates saved. The next send will use this copy.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  if (!templates) return null;

  return (
    <section className="bg-white border border-slate-200 rounded-md p-5 shadow-sm space-y-3" data-testid="email-templates-panel">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h3 className="text-sm font-semibold text-slate-900 flex items-center gap-2">
          <Mail className="w-4 h-4" />Email templates
        </h3>
        <div className="flex gap-2">
          <Button variant="ghost" size="sm" onClick={load} data-testid="email-templates-reload"><RefreshCw className="w-3.5 h-3.5 mr-1.5" />Reload</Button>
          <Button size="sm" onClick={save} disabled={saving} data-testid="email-templates-save">{saving ? "Saving…" : "Save templates"}</Button>
        </div>
      </div>
      <p className="text-xs text-slate-500">Everything inside <span className="font-mono">{"{curly braces}"}</span> gets replaced with the real value at send time. Leave a field blank to hide that paragraph.</p>

      <Tabs defaultValue="invoice" className="w-full">
        <TabsList className="mb-3 flex-wrap gap-1">
          {Object.entries(TEMPLATE_META).map(([k, m]) => (
            <TabsTrigger key={k} value={k} data-testid={`email-templates-tab-${k}`}>{m.label}</TabsTrigger>
          ))}
        </TabsList>
        {Object.entries(TEMPLATE_META).map(([k, m]) => (
          <TabsContent key={k} value={k} className="space-y-3">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
              <div className="lg:col-span-2 space-y-3">
                <div>
                  <Label className="text-xs uppercase text-slate-500">Subject</Label>
                  <Input value={templates[k].subject || ""} onChange={(e) => setField(k, "subject", e.target.value)} data-testid={`template-${k}-subject`} />
                </div>
                <div>
                  <Label className="text-xs uppercase text-slate-500">Greeting</Label>
                  <Input value={templates[k].greeting || ""} onChange={(e) => setField(k, "greeting", e.target.value)} data-testid={`template-${k}-greeting`} />
                </div>
                <div>
                  <Label className="text-xs uppercase text-slate-500">Body</Label>
                  <Textarea rows={5} value={templates[k].body || ""} onChange={(e) => setField(k, "body", e.target.value)} data-testid={`template-${k}-body`} />
                </div>
                <div>
                  <Label className="text-xs uppercase text-slate-500">Sign-off</Label>
                  <Textarea rows={3} value={templates[k].signoff || ""} onChange={(e) => setField(k, "signoff", e.target.value)} data-testid={`template-${k}-signoff`} />
                </div>
              </div>
              <div className="lg:col-span-1">
                <Label className="text-xs uppercase text-slate-500">Available tokens</Label>
                <div className="mt-1 space-y-1 border border-slate-200 rounded p-3 bg-slate-50">
                  {m.tokens.map(([tok, desc]) => (
                    <div key={tok} className="flex items-baseline justify-between gap-2 text-xs">
                      <button type="button" onClick={() => navigator.clipboard.writeText(tok)} className="font-mono text-emerald-700 hover:underline" title="Copy" data-testid={`template-${k}-token-${tok.replace(/[{}]/g,"")}`}>{tok}</button>
                      <span className="text-slate-500 text-right">{desc}</span>
                    </div>
                  ))}
                </div>
                <p className="text-[11px] text-slate-500 mt-2">Click a token to copy it — paste into any field above.</p>
              </div>
            </div>
          </TabsContent>
        ))}
      </Tabs>
    </section>
  );
}
